import React, { useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
import { styled } from '@compiled/react';

import { defineMessages, useIntl } from 'react-intl';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import { IconButton } from '@atlaskit/button/new';
import Tooltip from '@atlaskit/tooltip';

type SelectToggleIconProps = {
	isSelected: boolean;
	onClick: () => void;
	title: string;
};

const i18n = defineMessages({
	select: {
		id: 'side-navigation.select-toggle-icon.select',
		defaultMessage: 'Select {title}',
		description: 'Accessible label for the button that selects an item or group',
	},
	deselect: {
		id: 'side-navigation.select-toggle-icon.deselect',
		defaultMessage: 'Deselect {title}',
		description: 'Accessible label for the button that deselects an item or group',
	},
	accept: {
		id: 'side-navigation.review-content-suggestions.select-toggle-icon.accept',
		defaultMessage: 'Accept',
		description: 'Tooltip for the accept/select button on a suggestion row',
	},
	dismiss: {
		id: 'side-navigation.review-content-suggestions.select-toggle-icon.dismiss',
		defaultMessage: 'Dismiss',
		description: 'Tooltip for the dismiss/deselect button on a suggestion row',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const Wrapper = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& button:hover': {
		backgroundColor: 'transparent',
	},
});

export const SelectToggleIcon = ({ isSelected, onClick, title }: SelectToggleIconProps) => {
	const { formatMessage } = useIntl();
	const [isHovered, setIsHovered] = useState(false);

	const showCheckIcon = isSelected && !isHovered;

	return (
		<Tooltip content={isSelected ? formatMessage(i18n.dismiss) : formatMessage(i18n.accept)}>
			{(tooltipProps) => (
				<Wrapper
					{...tooltipProps}
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
				>
					<IconButton
						icon={(iconProps) =>
							showCheckIcon ? (
								<CheckCircleIcon {...iconProps} size="medium" />
							) : (
								<CrossCircleIcon {...iconProps} size="medium" />
							)
						}
						onClick={onClick}
						label={formatMessage(isSelected ? i18n.deselect : i18n.select, { title })}
						appearance="subtle"
						spacing="compact"
					/>
				</Wrapper>
			)}
		</Tooltip>
	);
};
